import { useState } from "react";
import { api } from "../lib/api";
import { trackEvent } from "../lib/tracking";
import { Phone, Check } from "lucide-react";

/**
 * Short callback form (Nom + Téléphone + Email + Activité + source).
 * Designed to be embedded at the bottom of Loiret/city SEO pages for
 * higher conversion than the full contact form.
 *
 * Props:
 *   - source: free-text label tracked with the lead (e.g. "loiret-hub", "orleans")
 *   - title:  optional override
 */
export const QuickCallbackForm = ({ source = "loiret", title }) => {
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", activity: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errMsg, setErrMsg] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setErrMsg("");
    try {
      await api.post("/leads/quick-callback", { ...form, source });
      trackEvent("form_submit", { form_id: "quick_callback", source });
      setStatus("success");
      setForm({ full_name: "", phone: "", email: "", activity: "" });
    } catch (e2) {
      setStatus("error");
      setErrMsg(e2?.response?.data?.detail || "Une erreur est survenue. Réessayez ou appelez le 02 38 74 50 60.");
    }
  };

  if (status === "success") {
    return (
      <div className="bg-emerald-50 border-2 border-emerald-600 p-6 md:p-8 text-center" data-testid="quick-callback-success">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-600 text-white mb-3">
          <Check className="w-6 h-6" strokeWidth={3} />
        </div>
        <h3 className="font-display font-black text-xl text-slate-900 mb-2">Demande reçue, merci !</h3>
        <p className="text-sm text-slate-700">Nous vous rappelons sous 24h ouvrées. Pour un échange immédiat : <a href="tel:0238745060" className="font-bold underline">02 38 74 50 60</a>.</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="bg-white border-2 border-slate-900 p-6 md:p-8 shadow-[8px_8px_0px_#0f172a]"
      data-testid="quick-callback-form"
    >
      <div className="flex items-center gap-2 mb-4">
        <Phone className="w-5 h-5 text-[#FFC107]" />
        <span className="text-xs uppercase tracking-widest font-bold text-slate-600">Rappel sous 24h</span>
      </div>
      <h3 className="font-display font-black text-2xl md:text-3xl text-slate-900 mb-2 leading-tight">
        {title || "Demandez à être rappelé"}
      </h3>
      <p className="text-sm text-slate-600 mb-6">
        Laissez-nous vos coordonnées : un interlocuteur unique vous recontacte sous 24h ouvrées. Sans agence, sans commission.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          required
          type="text"
          placeholder="Nom complet *"
          value={form.full_name}
          onChange={set("full_name")}
          className="border-2 border-slate-900 px-4 py-3 text-sm focus:outline-none focus:border-[#FFC107] bg-white"
          data-testid="quick-callback-name"
        />
        <input
          required
          type="tel"
          placeholder="Téléphone *"
          value={form.phone}
          onChange={set("phone")}
          className="border-2 border-slate-900 px-4 py-3 text-sm focus:outline-none focus:border-[#FFC107] bg-white"
          data-testid="quick-callback-phone"
        />
        <input
          required
          type="email"
          placeholder="Email *"
          value={form.email}
          onChange={set("email")}
          className="border-2 border-slate-900 px-4 py-3 text-sm focus:outline-none focus:border-[#FFC107] bg-white"
          data-testid="quick-callback-email"
        />
        <input
          type="text"
          placeholder="Activité (ex : logistique, atelier...)"
          value={form.activity}
          onChange={set("activity")}
          className="border-2 border-slate-900 px-4 py-3 text-sm focus:outline-none focus:border-[#FFC107] bg-white"
          data-testid="quick-callback-activity"
        />
      </div>
      {status === "error" && (
        <p className="text-sm text-red-600 mt-3" data-testid="quick-callback-error">{errMsg}</p>
      )}
      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 w-full md:w-auto bg-slate-900 text-white px-8 py-4 font-bold uppercase tracking-wider text-sm border-2 border-slate-900 hover:bg-[#FFC107] hover:text-slate-900 transition-colors disabled:opacity-50"
        data-testid="quick-callback-submit"
      >
        {status === "submitting" ? "Envoi en cours..." : "Être rappelé"}
      </button>
      <p className="text-xs text-slate-500 mt-3">Vos données ne sont utilisées que pour vous recontacter. Aucun envoi tiers.</p>
    </form>
  );
};

export default QuickCallbackForm;
